import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setDeleteMessage } from '../../actions/sendMessage';
import { retrySendMessageById } from '../../actions/sendMessage/retrySendMessageById';
import { DocumentMessage } from '../../components/DocumentMessage';
import { currentOpenChatIDSelector } from '../../selectors';
import { TPendingBlobFile } from '../../types/file';
import { TDocumentFile } from '../../types/messageFiles';
import { useApplicationContext } from '../../utils/applicationContext';
import { formatBytes } from '../../utils/formatBytes';
import { getFileSrc } from '../../utils/getFileSrc';

type TDocumentMessageContainerProps = {
  isOutcoming: boolean;
  isMessageLoading: boolean;
  isMessageFailed: boolean;
  document: TDocumentFile | null;
  documentDraft: TPendingBlobFile | null;
  messageId: string;
};

export const DocumentMessageContainer: React.FC<TDocumentMessageContainerProps> = ({
  isOutcoming,
  isMessageLoading,
  isMessageFailed,
  document,
  documentDraft,
  messageId,
}) => {
  const dispatch = useDispatch();

  const chatId = useSelector(currentOpenChatIDSelector);
  const { custom } = useApplicationContext();

  const handleCancel = React.useCallback(() => {
    if (chatId && documentDraft) {
      documentDraft.cancellationToken.cancel();
      dispatch(setDeleteMessage({ chatId, messageId, messagesMap: custom.messagesMap }));
    }
  }, [chatId, custom.messagesMap, dispatch, documentDraft, messageId]);

  const handleReload = React.useCallback(() => {
    dispatch(retrySendMessageById(chatId as string, messageId));
  }, [chatId, dispatch, messageId]);

  const src = React.useMemo(() => {
    if (document?.uid) {
      return getFileSrc(document.uid, chatId);
    }

    return null;
  }, [chatId, document?.uid]);

  const name = React.useMemo(() => {
    return document?.name || documentDraft?.file?.name || '';
  }, [document?.name, documentDraft?.file?.name]);

  const size = React.useMemo(() => {
    const sizeNumber = document?.size || documentDraft?.file?.size;

    return formatBytes(sizeNumber);
  }, [document?.size, documentDraft?.file?.size]);

  return (
    <DocumentMessage
      isOutcoming={isOutcoming}
      isMessageLoading={isMessageLoading}
      isMessageFailed={isMessageFailed}
      name={name}
      size={size}
      src={src}
      onCancel={handleCancel}
      onReload={handleReload}
    />
  );
};
